import React, { FC } from 'react';
import styled from '@emotion/styled';
import css from '@styled-system/css';
import shouldForwardProp from '@styled-system/should-forward-prop';
import PropTypes from 'prop-types';
import { variant } from 'styled-system';

import sx from '../../internal/styles/sx';
import { strikeThroughValues, StyledContainerProps } from '../RawMoney/types';
import BaseText from '../Text/BaseText';
import { toMoneyText } from './toMoneyText';
import { MoneyProps, moneySizes, moneyTypes } from './types';
import useMoneyFormatter from './useMoneyFormatter';
import strikeThrough from './variants/strikeThrough';
import types from './variants/typesVariants';

const MoneyPart = styled(BaseText, { shouldForwardProp })(
  ({ strikeThrough }) => {
    const isDefaultStrikeThrough = strikeThrough === 'default';
    return css({
      fontWeight: () => (isDefaultStrikeThrough ? 'default' : 'money.default'),
      letterSpacing: '0.5px',
      color: () => (isDefaultStrikeThrough ? 'neutrals.4' : 'money'),
    });
  },
  types
);

const DefaultPart = styled(MoneyPart, { shouldForwardProp })(() =>
  variant({
    prop: 'size',
    variants: {
      tiny: {
        fontSize: 'money.defaultPart.tiny',
        fontWeight: 'money.tiny',
      },
      small: { fontSize: 'money.defaultPart.small' },
      medium: { fontSize: 'money.defaultPart.medium' },
    },
  })
);

const NoIntegerPart = styled(MoneyPart)(
  variant({
    prop: 'size',
    variants: {
      tiny: {
        fontSize: 'money.nonIntegerPart.tiny',
        fontWeight: 'money.tiny',
      },
      small: { fontSize: 'money.nonIntegerPart.small' },
      medium: { fontSize: 'money.nonIntegerPart.medium' },
    },
  })
);

const componentParts = {
  default: DefaultPart,
  integer: DefaultPart,
  fraction: NoIntegerPart,
  currency: NoIntegerPart,
};

const StyledContainer = styled('span', {
  shouldForwardProp,
})<StyledContainerProps>(strikeThrough, sx, () =>
  css({ fontWeight: 'money.strikeThrough' })
);

const Money: FC<MoneyProps> = ({
  children: amount,
  size = moneySizes[0],
  strikeThrough = 'none',
  type = 'none',
  sx,
  rounded = false,
  ...props
}) => {
  const formatter = useMoneyFormatter({ ...props, rounded, styled: true });

  const moneyParts = formatter.format(amount);
  const lastPart = moneyParts[moneyParts.length - 1];
  const dir = lastPart?.value.endsWith('\u200f') ? 'rtl' : 'auto';
  const a11yContainerProps = {
    'aria-label': toMoneyText(moneyParts).replace(/\u00a0/g, ' '),
  };
  const a11yPartProps = { 'aria-hidden': true };

  return (
    <StyledContainer
      strikeThrough={strikeThrough}
      dir={dir}
      sx={sx}
      role="group"
      {...a11yContainerProps}
    >
      {moneyParts.map((part) => {
        const ComponentPart = componentParts[part.type];
        return (
          <ComponentPart
            strikeThrough={strikeThrough}
            size={size}
            type={type}
            key={part.type}
            className={`money-${part.type}`}
            {...a11yPartProps}
          >
            {part.value}
          </ComponentPart>
        );
      })}
    </StyledContainer>
  );
};

Money.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.string.isRequired,
    PropTypes.number.isRequired,
  ]).isRequired,
  currency: PropTypes.string.isRequired,
  rounded: PropTypes.bool,
  strikeThrough: PropTypes.oneOf(strikeThroughValues),
  size: PropTypes.oneOf(moneySizes),
  type: PropTypes.oneOf(moneyTypes),
  sx: PropTypes.object,
};

export default Money;
